import { ChangeEvent, FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ADMIN_ROUTES,
  PUBLIC_ROUTES,
  USER_ROUTES,
} from '../../constants/routeConstants';
import {
  VALIDATION_MESSAGES,
  REGEX_PATTERNS,
  VALIDATORS,
} from '../../utils/validationMessages';
import { toast } from 'react-hot-toast';
import PasswordInput from '../../components/common/PasswordInput';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import { Button, Input } from '../../components/ui';

interface LoginCredentials {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
}

const Login = () => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const isFormValid =
    credentials.email.trim() !== '' && credentials.password.trim() !== '';

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!credentials.email) {
      newErrors.email = VALIDATION_MESSAGES.email.required;
    } else if (!REGEX_PATTERNS.email.test(credentials.email)) {
      newErrors.email = VALIDATION_MESSAGES.email.invalid;
    } else if (!VALIDATORS.email(credentials.email)) {
      newErrors.email = VALIDATION_MESSAGES.email.invalid;
    } else if (credentials.email.length > 100) {
      newErrors.email = VALIDATION_MESSAGES.email.maxLength;
    }

    if (!credentials.password) {
      newErrors.password = VALIDATION_MESSAGES.password.required;
    } else if (credentials.password.length < 8) {
      newErrors.password = VALIDATION_MESSAGES.password.minLength;
    } else if (credentials.password.length > 50) {
      newErrors.password = VALIDATION_MESSAGES.password.maxLength;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await login(credentials);
      const successMessage = response?.message || 'Login successful!';
      toast.success(successMessage);

      if (response.user?.role === 'super_admin') {
        navigate(ADMIN_ROUTES.ADMIN_DASHBOARD);
      } else {
        navigate(USER_ROUTES.USER_DASHBOARD);
      }
    } catch (error: any) {
      console.error('Login error:', error);

      let errorMessage = 'Login failed';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.dismiss();
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev: LoginCredentials) => ({
      ...prev,
      [name]: value,
    }));

    const newErrors: FormErrors = { ...errors };

    if (name === 'email') {
      if (!value) {
        newErrors.email = VALIDATION_MESSAGES.email.required;
      } else if (!REGEX_PATTERNS.email.test(value)) {
        newErrors.email = VALIDATION_MESSAGES.email.invalid;
      } else if (!VALIDATORS.email(value)) {
        newErrors.email = VALIDATION_MESSAGES.email.invalid;
      } else if (value.length > 100) {
        newErrors.email = VALIDATION_MESSAGES.email.maxLength;
      } else {
        newErrors.email = undefined;
      }
    }

    if (name === 'password') {
      if (!value) {
        newErrors.password = VALIDATION_MESSAGES.password.required;
      } else if (value.length < 8) {
        newErrors.password = VALIDATION_MESSAGES.password.minLength;
      } else if (value.length > 20) {
        newErrors.password = VALIDATION_MESSAGES.password.maxLength;
      } else {
        newErrors.password = undefined;
      }
    }

    setErrors(newErrors);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCredentials((prev: LoginCredentials) => ({
      ...prev,
      password: value,
    }));

    const newErrors: FormErrors = { ...errors };

    if (!value) {
      newErrors.password = VALIDATION_MESSAGES.password.required;
    } else if (value.length < 8) {
      newErrors.password = VALIDATION_MESSAGES.password.minLength;
    } else if (value.length > 20) {
      newErrors.password = VALIDATION_MESSAGES.password.maxLength;
    } else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        value
      )
    ) {
      newErrors.password = VALIDATION_MESSAGES.password.weak;
    } else {
      newErrors.password = undefined;
    }

    setErrors(newErrors);
  };

  return (
    <LoaderOverlay isLoading={loading} loadingText="Signing in...">
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Sign in to your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Or{' '}
              <Link
                to={PUBLIC_ROUTES.SIGNUP}
                className="font-medium text-base text-blue-600 hover:text-blue-500 px-2 py-1 rounded hover:bg-blue-50 transition-colors duration-200"
              >
                create a new account
              </Link>
            </p>
          </div>

          <div className="bg-white py-8 px-6 shadow rounded-lg">
            <form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-6 text-left"
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={credentials.email}
                  onChange={handleChange}
                  className={`mt-1 block w-full px-3 py-1.5 border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:shadow-lg ${
                    errors.email
                      ? 'border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-400 hover:border-gray-500 bg-white'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <PasswordInput
                  id="password"
                  name="password"
                  label="Password"
                  value={credentials.password}
                  onChange={handlePasswordChange}
                  error={errors.password}
                  showToggle={true}
                  autoComplete="current-password"
                />
              </div>

              <div className="flex items-center justify-end">
                {/* <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Remember me
                </label>
              </div> */}

                <div>
                  <Link
                    to={PUBLIC_ROUTES.FORGOT_PASSWORD}
                    className="font-medium text-blue-600 hover:text-blue-500 text-sm px-2 py-1 rounded hover:bg-blue-50 transition-colors duration-200"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-semibold text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:bg-blue-600 transition-colors duration-200"
                  disabled={loading || !isFormValid}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </LoaderOverlay>
  );
};

export default Login;
